import React, { FC, useEffect, useRef, useState, useCallback } from 'react';

import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/types/base';
import { useDispatch, useSelector } from 'react-redux';

import { MACRO_TIMEOUT, MICRO_TIMEOUT } from 'constants/constants';
import playIcon from 'resources/img/playIcon.svg';
import {
  SetPlayingSec,
  SetPoorPerformancePlayedSec,
} from 'store/actions/video.action';
import { AppStateType } from 'store/reducers';

let timer: ReturnType<typeof setTimeout>;
let lastPlayedSeconds = 0;

const PlayerWrapper: FC<any> = (params) => {
  const dispatch = useDispatch();

  // Zoom & Pan state
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState<number>(1); // 1..8
  const [offset, setOffset] = useState<{
    x: number;
    y: number;
  }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  // Intrinsic video size (for bounds)
  const [videoMeta, setVideoMeta] = useState<{ w: number; h: number } | null>(
    null,
  );

  const clamp = (val: number, min: number, max: number) =>
    Math.min(Math.max(val, min), max);

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Base rendered content size (height fixed to container height; width by aspect)
  const getBaseContentSize = useCallback(() => {
    const containerH =
      (containerRef.current?.clientHeight ?? 0) ||
      Number(params.resizableHeight) ||
      0;
    const aspect = videoMeta ? videoMeta.w / videoMeta.h : 16 / 9; // fallback aspect
    const baseW = containerH * aspect;
    const baseH = containerH;
    return { baseW, baseH };
  }, [videoMeta, params.resizableHeight]);

  const clampAxis = (
    offsetVal: number,
    scaledSize: number,
    containerSize: number,
  ) => {
    if (scaledSize <= containerSize) {
      // center when smaller
      return (containerSize - scaledSize) / 2;
    }
    const min = containerSize - scaledSize; // negative
    const max = 0;
    return clamp(offsetVal, min, max);
  };

  const clampToBounds = useCallback(
    (x: number, y: number, nextScale?: number) => {
      const currentScale = nextScale ?? scale;
      const containerW = containerRef.current?.clientWidth ?? 0;
      const containerH = containerRef.current?.clientHeight ?? 0;
      const { baseW, baseH } = getBaseContentSize();
      const scaledW = baseW * currentScale;
      const scaledH = baseH * currentScale;

      const cx = clampAxis(x, scaledW, containerW);
      const cy = clampAxis(y, scaledH, containerH);
      return { x: cx, y: cy };
    },
    [scale, getBaseContentSize],
  );

  const { localVideoObjectURL } = useSelector(
    (state: AppStateType) => state.videoReducer,
  );

  const handleProgress = (state: OnProgressProps) => {
    dispatch(SetPoorPerformancePlayedSec(state.playedSeconds));
    lastPlayedSeconds = state.playedSeconds;
    if (timer) {
      return;
    }
    timer = setTimeout(() => {
      dispatch(SetPlayingSec(lastPlayedSeconds));
      //@ts-expect-error
      timer = undefined;
    }, MACRO_TIMEOUT);
  };

  // Wheel zoom: zoom toward cursor
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (!containerRef.current) {
        return;
      }
      e.preventDefault();

      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const delta = -e.deltaY / 1000; // wheel up -> zoom in
      const scaleChange = 1 + delta;
      const newScale = clamp(scale * scaleChange, 1, 8);

      // Anchor zoom to cursor
      const factor = newScale / scale;
      const newOffsetX = mouseX - factor * (mouseX - offset.x);
      const newOffsetY = mouseY - factor * (mouseY - offset.y);

      setScale(newScale);
      setOffset(clampToBounds(newOffsetX, newOffsetY, newScale));
    },
    [scale, offset, clampToBounds],
  );

  // Mouse pan
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX - offset.x,
        y: e.clientY - offset.y,
      };
    },
    [offset],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPanning) {
        return;
      }
      e.preventDefault();
      const newX = e.clientX - panStartRef.current.x;
      const newY = e.clientY - panStartRef.current.y;
      setOffset(clampToBounds(newX, newY));
    },
    [isPanning, clampToBounds],
  );

  const endPan = useCallback(() => setIsPanning(false), []);

  // Double click to reset
  const handleDoubleClick = useCallback(() => {
    resetView();
  }, [resetView]);

  // Basic touch support: one finger pan, two finger pinch
  const touchStateRef = useRef<{
    dist: number | null;
    cx: number;
    cy: number;
  }>({ dist: null, cx: 0, cy: 0 });

  const getTouchCenterAndDistance = (touches: React.TouchList) => {
    const t1 = touches[0];
    const t2 = touches[1];
    const cx = (t1.clientX + t2.clientX) / 2;
    const cy = (t1.clientY + t2.clientY) / 2;
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    const dist = Math.hypot(dx, dy);
    return { cx, cy, dist };
  };

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 1) {
        setIsPanning(true);
        panStartRef.current = {
          x: e.touches[0].clientX - offset.x,
          y: e.touches[0].clientY - offset.y,
        };
      } else if (e.touches.length === 2) {
        const { cx, cy, dist } = getTouchCenterAndDistance(e.touches);
        touchStateRef.current = { dist, cx, cy };
      }
    },
    [offset],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (e.touches.length === 1 && isPanning) {
        const newX = e.touches[0].clientX - panStartRef.current.x;
        const newY = e.touches[0].clientY - panStartRef.current.y;
        setOffset(clampToBounds(newX, newY));
      } else if (e.touches.length === 2) {
        e.preventDefault();
        if (!containerRef.current) {
          return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const { cx, cy, dist } = getTouchCenterAndDistance(e.touches);
        const prev = touchStateRef.current;
        if (prev.dist && dist) {
          const pinchChange = dist / prev.dist;
          const newScale = clamp(scale * pinchChange, 1, 8);
          const localX = cx - rect.left;
          const localY = cy - rect.top;

          // Anchor zoom to gesture center
          const factor = newScale / scale;
          const newOffsetX = localX - factor * (localX - offset.x);
          const newOffsetY = localY - factor * (localY - offset.y);

          setScale(newScale);
          setOffset(clampToBounds(newOffsetX, newOffsetY, newScale));
        }
        touchStateRef.current = { dist, cx, cy };
      }
    },
    [isPanning, scale, offset, clampToBounds],
  );

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    touchStateRef.current.dist = null;
  }, []);

  useEffect(() => {
    const reClamp = () => {
      setOffset((prev) => clampToBounds(prev.x, prev.y));
    };
    window.addEventListener('resize', reClamp);
    reClamp();
    return () => {
      window.removeEventListener('resize', reClamp);
    };
  }, [scale, videoMeta, clampToBounds]);

  useEffect(() => {
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const handleReady = useCallback(() => {
    const videoEl = containerRef.current?.querySelector(
      'video',
    ) as HTMLVideoElement | null;
    if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
      setVideoMeta({ w: videoEl.videoWidth, h: videoEl.videoHeight });
    }
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: params.resizableHeight,
        width: '100%',
        touchAction: 'none', // allows pinch/drag
        background: '#000',
        userSelect: 'none',
        cursor: isPanning ? 'grabbing' : scale > 1 ? 'grab' : 'default',
        zIndex: 0,
      }}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={endPan}
      onMouseLeave={endPan}
      onDoubleClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <div
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          transformOrigin: '0 0',
          willChange: 'transform',
          display: 'inline-block',
        }}
      >
        <ReactPlayer
          config={{
            file: {
              attributes: {
                preload: 'metadata',
                type: 'video/mp4',
              },
              hlsOptions: {
                forceHLS: false,
                xhrSetup: () => {},
              },
            },
          }}
          ref={params.refPlayer}
          height={params.resizableHeight}
          width={'auto'}
          url={localVideoObjectURL != '' ? localVideoObjectURL : params.url}
          playIcon={playIcon}
          playbackRate={params.playbackRate}
          progressInterval={MICRO_TIMEOUT}
          playing={params.isPlaying}
          onProgress={handleProgress}
          onError={params.handleError}
          onReady={handleReady}
        />
      </div>
    </div>
  );
};
export default PlayerWrapper;
